import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Wand2, Globe, Sparkles, Crown, Monitor, Tablet, Smartphone, Maximize2,
  RefreshCw, Download, Share2, GitBranch, ChevronRight, Layers,
  FileCode, Palette, Package, MessageSquarePlus, ExternalLink, GraduationCap, Rocket,
  Users, Flag, Send,
} from "lucide-react";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ReadAlongside from "../components/ReadAlongside";
import UpgradeNudge from "../components/UpgradeNudge";
import LearnWebsiteDialog from "../components/LearnWebsiteDialog";
import DeployToVercelDialog from "../components/DeployToVercelDialog";
import DeploymentsPanel from "../components/DeploymentsPanel";
import AnalyticsPanel from "../components/AnalyticsPanel";

const EXAMPLES = [
  "A neighbourhood bakery in Portland called Rise & Rye — sourdough-focused, warm editorial feel.",
  "A minimalist portfolio for a Tokyo product designer named Aiko Tanaka — case studies, notebook journal.",
  "A landing page for a solo indie SaaS that generates weekly retros for engineering teams.",
  "A cinematic marketing site for a Kerala eco-resort with a booking form and photo gallery.",
];

const STYLES = ["Editorial", "Playful", "Minimal", "Bold", "Warm", "Cinematic", "Corporate"];

export default function WebsiteStudio() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();

  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState("");
  const [audience, setAudience] = useState("");
  const [bp, setBp] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refining, setRefining] = useState(false);
  const [refineText, setRefineText] = useState("");
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [usage, setUsage] = useState(null);
  const [device, setDevice] = useState("desktop"); // desktop | tablet | mobile
  const [exporting, setExporting] = useState(false);
  const [learnOpen, setLearnOpen] = useState(false);
  const [deployOpen, setDeployOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [communityTemplates, setCommunityTemplates] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const [deployKey, setDeployKey] = useState(0);
  const iframeRef = useRef(null);

  useEffect(() => {
    api.get("/website/templates").then(({ data }) => setTemplates(data)).catch(() => {});
    api.get("/website/community-templates").then(({ data }) => setCommunityTemplates(data || [])).catch(() => {});
  }, []);

  const remixCommunity = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/website/community-templates/${id}/remix`);
      setBp(data);
      setSaved(false);
      toast.success(`Remixed: ${data.title}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remix that template.");
    } finally { setBusy(false); }
  };

  const publishToCommunity = async () => {
    if (!user) {
      toast("Log in to publish", { action: { label: "Log in", onClick: () => nav("/login") } });
      return;
    }
    if (!saved) {
      toast.error("Save your site first, then publish.");
      return;
    }
    setPublishing(true);
    try {
      await api.post(`/website/projects/${bp.id}/publish`);
      setBp((prev) => ({ ...prev, is_public: true, is_template: true }));
      toast.success("Published to Community Templates 🌿");
      // refresh gallery
      api.get("/website/community-templates").then(({ data }) => setCommunityTemplates(data || [])).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not publish.");
    } finally { setPublishing(false); }
  };

  const reportCommunity = async (id) => {
    const reason = window.prompt("What's wrong with this template? (spam, hateful, low quality...)");
    if (!reason || !reason.trim()) return;
    try {
      await api.post(`/website/community-templates/${id}/report`, { reason: reason.trim() });
      toast.success("Reported — an admin will review it.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not report.");
    }
  };

  const startFromTemplate = async (templateId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/website/templates/${templateId}/start`);
      setBp(data);
      setSaved(false);
      toast.success(`Started from template: ${data.title}`);
    } catch {
      toast.error("Could not load template.");
    } finally { setBusy(false); }
  };

  useEffect(() => {
    const seed = params.get("prompt");
    if (seed) setPrompt(seed);
    const tplId = params.get("template");
    if (tplId) startFromTemplate(tplId);
    const savedId = params.get("blueprint_id");
    if (savedId && user) {
      api.get(`/website/projects/${savedId}`)
        .then(({ data }) => {
          setBp(data);
          setSaved(true);
          toast.success(`Resumed: ${data.title}`);
        })
        .catch(() => toast.error("Could not load that website."));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, user]);

  useEffect(() => {
    if (user) api.get("/prompt/usage").then(({ data }) => setUsage(data)).catch(() => {});
  }, [user]);

  const generate = async () => {
    if (prompt.trim().length < 10) return toast.error("Describe your website in at least a sentence.");
    setBusy(true); setBp(null); setSaved(false);
    try {
      const { data: startData } = await api.post("/website/plan", {
        prompt, style: style || undefined, audience: audience || undefined,
      });
      const jobId = startData.job_id;
      const finalBp = await pollJob(`/website/plan/${jobId}`);
      setBp(finalBp);
      toast.success("Website generated ✨");
      if (user) api.get("/prompt/usage").then(({ data }) => setUsage(data)).catch(() => {});
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) toast.error(err.response?.data?.detail || "You've used all your prompts this month.");
      else toast.error(err.message || err.response?.data?.detail || "Generation failed. Try again.");
    } finally { setBusy(false); }
  };

  const refine = async (text) => {
    const follow = (text || refineText).trim();
    if (follow.length < 4) return toast.error("Tell Durga what to change.");
    setRefining(true);
    try {
      const { data: startData } = await api.post("/website/refine", {
        blueprint: bp,
        follow_up: follow,
        history: bp?.refine_turns || [],
      });
      const jobId = startData.job_id;
      const finalBp = await pollJob(`/website/refine/${jobId}`);
      setBp(finalBp);
      setSaved(false);
      setRefineText("");
      toast.success("Refined ✨");
      if (user) api.get("/prompt/usage").then(({ data }) => setUsage(data)).catch(() => {});
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) toast.error(err.response?.data?.detail || "Out of prompts this month.");
      else toast.error(err.message || err.response?.data?.detail || "Refinement failed. Try again.");
    } finally { setRefining(false); }
  };

  // Poll a job endpoint every 3s for up to 5 minutes.
  const pollJob = async (path) => {
    const maxAttempts = 100; // ~5 min at 3s
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, 3000));
      const { data } = await api.get(path);
      if (data.status === "ready") return data.blueprint;
      if (data.status === "error") throw new Error(data.error || "Generation failed");
    }
    throw new Error("Timed out — please try again");
  };

  const save = async () => {
    if (!user) {
      toast("Log in to save your website", { action: { label: "Log in", onClick: () => nav("/login") } });
      return;
    }
    setSaving(true);
    try {
      await api.post("/website/save", { blueprint: bp });
      setSaved(true);
      toast.success("Saved to your dashboard");
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  const exportZip = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/website/export`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(localStorage.getItem("dpa_token") ? { Authorization: `Bearer ${localStorage.getItem("dpa_token")}` } : {}),
        },
        body: JSON.stringify({ blueprint: bp }),
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(bp.title || "MySite").replace(/\s+/g, "")}-website.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Next.js starter downloaded");
    } catch { toast.error("Could not export"); }
    finally { setExporting(false); }
  };

  const openPreviewNewTab = () => {
    if (!bp?.preview_html) return;
    const blob = new Blob([bp.preview_html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener");
  };

  const branchIt = () => {
    if (!bp) return;
    setBp({
      ...bp,
      id: undefined,
      saved: false,
      is_public: false,
      refine_turns: [],
      title: bp.title?.endsWith("(branch)") ? bp.title : `${bp.title} (branch)`,
      created_at: new Date().toISOString(),
    });
    setSaved(false);
    toast.success("Branched — iterate freely");
  };

  const deviceFrame = device === "mobile"
    ? "max-w-[390px]"
    : device === "tablet"
      ? "max-w-[820px]"
      : "max-w-full";

  return (
    <div className="container-narrow py-10 md:py-16" data-testid="website-studio-page">
      {/* Hero */}
      <div className="text-center mb-10">
        <div className="label-eyebrow mb-3 inline-flex items-center gap-2 justify-center">
          <Globe className="w-3.5 h-3.5" /> Website Studio
        </div>
        <h1 className="font-heading text-4xl md:text-6xl font-semibold leading-tight tracking-tight">
          Build a website <span className="italic text-marigold">with words.</span>
        </h1>
        <p className="text-ink2 mt-4 max-w-2xl mx-auto">
          Describe your idea, portfolio, business, or online experience. Durga plans the pages, shapes the
          visual language, drafts real copy, and generates a responsive site you can refine, study, and ship.
        </p>
      </div>

      {usage && (
        <div className="max-w-3xl mx-auto mb-4 flex items-center justify-between text-sm" data-testid="website-usage">
          <div className="inline-flex items-center gap-2 text-ink2">
            <Crown className="w-4 h-4 text-saffron" />
            <span className="capitalize">{usage.tier}</span> tier ·
            {usage.unlimited ? (
              <span className="text-teal font-medium ml-1">unlimited generations</span>
            ) : (
              <span className="ml-1">
                <span className="font-medium text-ink">{usage.remaining}</span> of {usage.limit} prompts left this month
              </span>
            )}
          </div>
          {!usage.unlimited && usage.remaining <= 1 && (
            <button onClick={() => nav("/pricing")} className="text-marigold font-medium hover:underline" data-testid="website-upgrade-link">
              Upgrade →
            </button>
          )}
        </div>
      )}

      <UpgradeNudge usage={usage} variant="app" />

      {/* Describe (before generation) */}
      {!bp && (
        <div className="card-soft p-5 md:p-6 mb-6" data-testid="website-describe-form">
          <textarea
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            rows={4}
            maxLength={3000}
            placeholder="e.g. A landing page for a Kerala eco-resort — cinematic photos, booking CTA, story-first tone."
            className="w-full bg-transparent text-ink text-lg placeholder:text-ink2/60 outline-none resize-none"
            data-testid="website-prompt-textarea"
          />
          <div className="grid md:grid-cols-2 gap-3 mt-4 pt-4 border-t border-border">
            <div>
              <label className="text-xs uppercase tracking-wider text-ink2 font-semibold">Audience <span className="opacity-60">(optional)</span></label>
              <input
                value={audience}
                onChange={e => setAudience(e.target.value)}
                placeholder="e.g. Portland locals, remote workers"
                className="w-full mt-1 bg-paper border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-ink"
                data-testid="website-audience-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-ink2 font-semibold">Visual style <span className="opacity-60">(optional)</span></label>
              <div className="mt-1 flex flex-wrap gap-1.5" data-testid="website-style-chips">
                {STYLES.map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setStyle(style === s ? "" : s)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition ${style === s ? "bg-ink text-cream border-ink" : "bg-paper border-border hover:border-ink"}`}
                    data-testid={`website-style-${s.toLowerCase()}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 pt-4 mt-3 border-t border-border">
            <div className="text-xs text-ink2">
              {prompt.length} / 3000 chars · powered by Claude Sonnet 4.5
            </div>
            <button onClick={generate} className="btn-primary" disabled={busy} data-testid="website-generate-button">
              <Wand2 className="w-4 h-4" /> {busy ? "Generating…" : "Generate Website"}
            </button>
          </div>
        </div>
      )}

      {/* Read alongside — chapters from the curriculum that pair with a website brief */}
      {!bp && !busy && (
        <div className="mb-6">
          <ReadAlongside
            seeds={prompt || "website html forms deployment state routes"}
            eyebrow="Read alongside as you build"
            title="chapters from the curriculum for this website"
            askPrompt={prompt ? `As I describe "${prompt.slice(0, 200)}" for Website Studio, what curriculum ideas will help me most?` : `What chapters from the curriculum help me plan a great website in Website Studio?`}
            defaultOpen={false}
            testId="website-read-alongside"
          />
        </div>
      )}

      {!bp && !busy && (
        <div className="mb-10 space-y-8">
          {templates.length > 0 && (
            <div data-testid="website-templates-gallery">
              <div className="flex items-center justify-between mb-3">
                <div className="label-eyebrow">Start from a template</div>
                <div className="text-xs text-ink2">{templates.length} ready to remix</div>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {templates.map((t) => (
                  <TemplateCard key={t.id} template={t} onStart={() => startFromTemplate(t.id)} />
                ))}
              </div>
            </div>
          )}

          {communityTemplates.length > 0 && (
            <div data-testid="website-community-templates">
              <div className="flex items-center justify-between mb-3">
                <div className="label-eyebrow inline-flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" /> Community remixes
                </div>
                <div className="text-xs text-ink2">{communityTemplates.length} published</div>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {communityTemplates.slice(0, 8).map((ct) => (
                  <CommunityCard
                    key={ct.id}
                    template={ct}
                    onRemix={() => remixCommunity(ct.id)}
                    onReport={() => reportCommunity(ct.id)}
                  />
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="label-eyebrow mb-3">Or start from a sentence</div>
            <div className="grid md:grid-cols-2 gap-3" data-testid="website-examples">
              {EXAMPLES.map((ex, i) => (
                <button
                  key={i}
                  onClick={() => setPrompt(ex)}
                  className="text-left text-sm p-4 bg-paper rounded-2xl border-2 border-border hover:border-saffron hover:bg-white transition group"
                  data-testid={`website-example-${i}`}
                >
                  <span className="text-ink group-hover:text-marigold transition">{ex}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {busy && <GeneratingSkeleton />}

      {/* Three-panel workspace */}
      {bp && (
        <div className="grid lg:grid-cols-12 gap-4" data-testid="website-workspace">
          {/* LEFT: Project structure */}
          <aside className="lg:col-span-3 space-y-3">
            <StructurePanel bp={bp} />
            <AnalyticsPanel blueprintId={bp.id} saved={saved} />
            <DeploymentsPanel blueprintId={bp.id} refreshKey={deployKey} />
          </aside>

          {/* CENTER: Preview */}
          <main className="lg:col-span-6 space-y-3">
            <div className="card-soft p-3 flex items-center justify-between gap-2 flex-wrap" data-testid="website-preview-toolbar">
              <div className="flex items-center gap-1">
                <ToolbarBtn active={device === "desktop"} onClick={() => setDevice("desktop")} icon={<Monitor className="w-4 h-4" />} label="Desktop" testid="device-desktop" />
                <ToolbarBtn active={device === "tablet"} onClick={() => setDevice("tablet")} icon={<Tablet className="w-4 h-4" />} label="Tablet" testid="device-tablet" />
                <ToolbarBtn active={device === "mobile"} onClick={() => setDevice("mobile")} icon={<Smartphone className="w-4 h-4" />} label="Mobile" testid="device-mobile" />
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => iframeRef.current && (iframeRef.current.srcdoc = bp.preview_html)} className="btn-ghost text-xs" title="Refresh preview" data-testid="preview-refresh">
                  <RefreshCw className="w-3.5 h-3.5" />
                </button>
                <button onClick={openPreviewNewTab} className="btn-ghost text-xs" title="Open in new tab" data-testid="preview-fullscreen">
                  <Maximize2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            <div className="card-soft p-4 bg-ink/5" data-testid="website-preview-frame">
              <div className={`mx-auto transition-all ${deviceFrame}`}>
                <div className="rounded-2xl overflow-hidden border-2 border-ink shadow-[6px_6px_0_0_#1A1B41] bg-white">
                  <iframe
                    ref={iframeRef}
                    title="Website preview"
                    srcDoc={bp.preview_html || "<html><body style='padding:2rem;font-family:sans-serif'>No preview HTML generated.</body></html>"}
                    sandbox="allow-scripts"
                    className="w-full block"
                    style={{ height: "70vh" }}
                    data-testid="website-preview-iframe"
                  />
                </div>
              </div>
            </div>

            <ActionBar
              bp={bp}
              saved={saved}
              saving={saving}
              exporting={exporting}
              onSave={save}
              onExport={exportZip}
              onBranch={branchIt}
              onOpenNewTab={openPreviewNewTab}
              onDeploy={() => {
                if (!user) {
                  toast("Log in to deploy to Vercel", { action: { label: "Log in", onClick: () => nav("/login") } });
                  return;
                }
                setDeployOpen(true);
              }}
              onLearn={() => setLearnOpen(true)}
              onPublish={publishToCommunity}
              publishing={publishing}
              isPublished={bp.is_template && bp.is_public}
            />
          </main>

          {/* RIGHT: Durga Assistant */}
          <aside className="lg:col-span-3 space-y-3">
            <DurgaAssistant
              bp={bp}
              text={refineText}
              onText={setRefineText}
              busy={refining}
              onSend={refine}
            />
            <LearningPanel bp={bp} onDeepDive={() => setLearnOpen(true)} />
          </aside>
        </div>
      )}

      <LearnWebsiteDialog open={learnOpen} onOpenChange={setLearnOpen} blueprint={bp} />
      <DeployToVercelDialog
        open={deployOpen}
        onOpenChange={(v) => {
          setDeployOpen(v);
          if (!v) setDeployKey((k) => k + 1); // refresh deployments panel on close
        }}
        blueprint={bp}
      />
    </div>
  );
}

// ---------- Sub-components ----------

function TemplateCard({ template, onStart }) {
  const audience = template.audience === "learner" ? "For learners" : "Professional";
  return (
    <button
      onClick={onStart}
      className="group relative overflow-hidden rounded-2xl border-2 border-border hover:border-ink transition text-left bg-cream"
      data-testid={`website-template-${template.id}`}
    >
      <div
        className="h-32 relative overflow-hidden"
        style={{ background: template.cover_gradient, color: template.cover_accent }}
      >
        <div className="absolute top-2 left-3 text-[10px] uppercase tracking-wider font-semibold opacity-80">
          {audience}
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className="font-heading text-2xl md:text-3xl font-semibold text-center leading-tight px-4"
            style={{ color: template.cover_accent, fontFamily: `'${template.typography?.heading || "Fraunces"}', Georgia, serif` }}
          >
            {template.name}
          </div>
        </div>
        <div className="absolute bottom-2 left-3 right-3 flex gap-1">
          {["primary", "accent", "muted", "cream"].map((k) => (
            <div key={k} className="flex-1 h-2 rounded-full border border-black/10" style={{ background: template.palette?.[k] }} />
          ))}
        </div>
      </div>
      <div className="p-3">
        <div className="text-xs font-semibold text-ink truncate">{template.name}</div>
        <div className="text-[11px] text-ink2 line-clamp-2 mt-0.5 leading-snug">{template.tagline}</div>
        <div className="text-[10px] uppercase tracking-wider text-ink2 mt-2 flex items-center gap-1">
          <Sparkles className="w-3 h-3 text-marigold group-hover:animate-pulse" />
          Start from this
        </div>
      </div>
    </button>
  );
}


function GeneratingSkeleton() {
  return (
    <div className="animate-pulse" data-testid="website-generating">
      <div className="card-soft p-6 mb-3">
        <div className="h-4 w-32 bg-paper rounded mb-3" />
        <div className="h-8 w-3/4 bg-paper rounded mb-2" />
        <div className="h-4 w-full bg-paper rounded" />
      </div>
      <div className="grid lg:grid-cols-12 gap-4">
        <div className="lg:col-span-3 card-soft h-96" />
        <div className="lg:col-span-6 card-soft h-96 bg-gradient-to-br from-paper via-cream to-marigold/5 flex items-center justify-center">
          <div className="text-center">
            <Sparkles className="w-8 h-8 mx-auto mb-2 text-marigold animate-pulse" />
            <div className="text-sm text-ink2">Durga is planning your site…</div>
          </div>
        </div>
        <div className="lg:col-span-3 card-soft h-96" />
      </div>
    </div>
  );
}

function StructurePanel({ bp }) {
  const [tab, setTab] = useState("pages");
  const ds = bp.design_system || {};
  const palette = ds.palette || {};
  return (
    <div className="card-soft p-4" data-testid="website-structure-panel">
      <div className="flex items-center gap-2 mb-3">
        <Layers className="w-4 h-4 text-marigold" />
        <h3 className="text-xs font-semibold uppercase tracking-wider text-ink">Project</h3>
      </div>
      <div className="text-sm font-heading font-semibold text-ink mb-1" data-testid="website-title">{bp.title}</div>
      {bp.tagline && <div className="text-xs text-ink2 mb-3 italic">&ldquo;{bp.tagline}&rdquo;</div>}

      <div className="flex gap-1 mb-2 border-b border-border">
        <TabBtn active={tab === "pages"} onClick={() => setTab("pages")} label="Pages" />
        <TabBtn active={tab === "design"} onClick={() => setTab("design")} label="Design" />
        <TabBtn active={tab === "seo"} onClick={() => setTab("seo")} label="SEO" />
      </div>

      {tab === "pages" && (
        <ul className="space-y-1 mt-2 max-h-96 overflow-y-auto" data-testid="website-pages-list">
          {(bp.sitemap || []).map((p, i) => (
            <li key={i} className="text-sm p-2 rounded-lg hover:bg-paper transition group cursor-pointer" data-testid={`website-page-${i}`}>
              <div className="flex items-center gap-2">
                <FileCode className="w-3.5 h-3.5 text-ink2 group-hover:text-marigold flex-shrink-0" />
                <span className="font-medium text-ink">{p.name}</span>
                <span className="text-xs text-ink2 ml-auto">{p.slug}</span>
              </div>
              {p.purpose && <div className="text-xs text-ink2 mt-1 ml-5 line-clamp-2">{p.purpose}</div>}
            </li>
          ))}
        </ul>
      )}

      {tab === "design" && (
        <div className="mt-2 space-y-3 text-xs" data-testid="website-design-panel">
          <div>
            <div className="text-ink2 uppercase tracking-wider mb-1.5 font-semibold">Palette</div>
            <div className="grid grid-cols-5 gap-1.5">
              {["primary", "accent", "ink", "cream", "muted"].map(k => (
                <div key={k} className="flex flex-col items-center gap-1">
                  <div className="w-full h-8 rounded border border-border" style={{ background: palette[k] || "#eee" }} />
                  <span className="text-[10px] text-ink2">{k}</span>
                </div>
              ))}
            </div>
          </div>
          {ds.typography && (
            <div>
              <div className="text-ink2 uppercase tracking-wider mb-1.5 font-semibold">Type</div>
              <div className="space-y-1">
                <div><span className="text-ink2">Heading:</span> <span className="text-ink font-medium">{ds.typography.heading}</span></div>
                <div><span className="text-ink2">Body:</span> <span className="text-ink font-medium">{ds.typography.body}</span></div>
              </div>
            </div>
          )}
          {bp.tone && (
            <div>
              <div className="text-ink2 uppercase tracking-wider mb-1.5 font-semibold">Tone</div>
              <div className="text-ink">{bp.tone}</div>
            </div>
          )}
          {bp.tech_stack && (
            <div>
              <div className="text-ink2 uppercase tracking-wider mb-1.5 font-semibold">Stack</div>
              <div className="text-ink">{[bp.tech_stack.framework, bp.tech_stack.styling].filter(Boolean).join(" · ")}</div>
            </div>
          )}
        </div>
      )}

      {tab === "seo" && (
        <div className="mt-2 space-y-2 text-xs" data-testid="website-seo-panel">
          <div>
            <div className="text-ink2 uppercase tracking-wider mb-1 font-semibold">Meta title</div>
            <div className="text-ink">{bp.seo?.title || "—"}</div>
          </div>
          <div>
            <div className="text-ink2 uppercase tracking-wider mb-1 font-semibold">Description</div>
            <div className="text-ink2 leading-relaxed">{bp.seo?.description || "—"}</div>
          </div>
          <div>
            <div className="text-ink2 uppercase tracking-wider mb-1 font-semibold">Keywords</div>
            <div className="flex flex-wrap gap-1">
              {(bp.seo?.keywords || []).map((k, i) => (
                <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-paper border border-border">{k}</span>
              ))}
              {(!bp.seo?.keywords || bp.seo.keywords.length === 0) && <span className="text-ink2">—</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      className={`text-xs px-3 py-1.5 -mb-px border-b-2 transition ${
        active ? "border-marigold text-ink font-semibold" : "border-transparent text-ink2 hover:text-ink"
      }`}
      data-testid={`website-tab-${label.toLowerCase()}`}
    >
      {label}
    </button>
  );
}

function ToolbarBtn({ active, onClick, icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition ${
        active ? "bg-ink text-cream" : "text-ink2 hover:bg-paper"
      }`}
      title={label}
      data-testid={testid}
    >
      {icon} <span className="hidden md:inline">{label}</span>
    </button>
  );
}

function ActionBar({ bp, saved, saving, exporting, onSave, onExport, onBranch, onOpenNewTab, onDeploy, onLearn, onPublish, publishing, isPublished }) {
  return (
    <div className="card-soft p-4 flex flex-wrap items-center gap-2" data-testid="website-action-bar">
      <button onClick={onDeploy} className="btn-primary text-sm" data-testid="website-deploy-button">
        <Rocket className="w-4 h-4" /> Deploy to Vercel
      </button>
      <button onClick={onExport} disabled={exporting} className="btn-outline-ink text-sm" data-testid="website-export-button">
        <Download className="w-4 h-4" /> {exporting ? "Packing…" : "Download ZIP"}
      </button>
      <button onClick={onLearn} className="btn-outline-ink text-sm" data-testid="website-learn-button">
        <GraduationCap className="w-4 h-4" /> Learn this website
      </button>
      <button
        onClick={onPublish}
        disabled={publishing || isPublished}
        className={`btn-outline-ink text-sm disabled:opacity-60 ${isPublished ? "text-teal border-teal/40" : ""}`}
        data-testid="website-publish-button"
        title={isPublished ? "Published to Community Templates" : "Publish to Community Templates"}
      >
        <Users className="w-4 h-4" />
        {isPublished ? "Published" : publishing ? "Publishing…" : "Publish"}
      </button>
      <button onClick={onSave} disabled={saving || saved} className="btn-ghost text-sm" data-testid="website-save-button">
        <Package className="w-4 h-4" /> {saved ? "Saved" : saving ? "Saving…" : "Save"}
      </button>
      <button onClick={onOpenNewTab} className="btn-ghost text-sm" data-testid="website-open-new-tab">
        <ExternalLink className="w-4 h-4" /> Preview
      </button>
      <button onClick={onBranch} className="btn-ghost text-sm" data-testid="website-branch">
        <GitBranch className="w-4 h-4" /> Branch
      </button>
    </div>
  );
}


function CommunityCard({ template, onRemix, onReport }) {
  const palette = template.design_system?.palette || {};
  const heading = template.design_system?.typography?.heading || "Fraunces";
  return (
    <div
      className="group relative overflow-hidden rounded-2xl border-2 border-border hover:border-ink transition text-left bg-cream"
      data-testid={`community-template-${template.id}`}
    >
      <button
        onClick={onRemix}
        className="w-full block h-32 relative overflow-hidden text-left"
        style={{ background: `linear-gradient(135deg, ${palette.cream || "#F9F6F0"} 0%, ${palette.muted || palette.accent || "#E9B979"} 55%, ${palette.primary || "#1F1A17"} 100%)` }}
      >
        <div className="absolute inset-0 flex items-center justify-center px-3">
          <div
            className="font-heading text-lg md:text-xl font-semibold text-center leading-tight"
            style={{ color: palette.cream || "#FFF8EC", fontFamily: `'${heading}', Georgia, serif`, textShadow: "0 1px 4px rgba(0,0,0,0.28)" }}
          >
            {template.title}
          </div>
        </div>
        <div className="absolute top-2 left-3 text-[10px] uppercase tracking-wider font-semibold text-cream/90">Community</div>
        {template.template_source_title && (
          <div className="absolute top-2 right-3 text-[9px] uppercase tracking-wider text-cream/80">
            ↳ {template.template_source_title.slice(0, 12)}
          </div>
        )}
        <div className="absolute bottom-2 left-3 right-3 flex gap-1">
          {["primary", "accent", "muted", "cream"].map((k) => (
            <div key={k} className="flex-1 h-2 rounded-full border border-black/10" style={{ background: palette[k] }} />
          ))}
        </div>
      </button>
      <div className="p-3">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-ink truncate">
          <span className="flex-1 truncate">{template.title}</span>
          {template.remix_count > 0 && (
            <span className="text-[10px] text-marigold font-semibold" title={`${template.remix_count} remixes`}>
              ↳ {template.remix_count}
            </span>
          )}
        </div>
        <div className="text-[11px] text-ink2 line-clamp-2 mt-0.5 leading-snug">{template.tagline}</div>
        <div className="flex items-center justify-between mt-2">
          <div className="text-[10px] text-ink2 truncate">by {template.author_name || "Anonymous"}</div>
          <div className="flex items-center gap-1">
            <button
              onClick={(e) => { e.stopPropagation(); onReport(); }}
              className="text-ink2 hover:text-marigold p-1"
              title="Report"
              aria-label="Report"
              data-testid={`community-report-${template.id}`}
            >
              <Flag className="w-3 h-3" />
            </button>
            <button
              onClick={onRemix}
              className="text-[10px] uppercase tracking-wider font-semibold text-marigold hover:text-ink px-2 py-1 border border-marigold/40 hover:border-ink rounded-full transition"
              data-testid={`community-remix-${template.id}`}
            >
              Remix →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DurgaAssistant({ bp, text, onText, busy, onSend }) {
  const turns = bp?.refine_turns || [];
  const listRef = useRef(null);
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [turns.length, busy]);

  const samples = [
    "Change the palette to a deep teal with a warm coral accent.",
    "Swap the heading font to Instrument Serif and tighten spacing.",
    "Add a 'Pricing' page with three simple tiers.",
    "Rewrite the hero copy — more punchy and less corporate.",
  ];

  return (
    <div className="card-soft p-4 bg-gradient-to-br from-cream via-paper to-marigold/5" data-testid="durga-assistant">
      <div className="flex items-center gap-2 mb-3">
        <MessageSquarePlus className="w-4 h-4 text-marigold" />
        <h3 className="text-xs font-semibold uppercase tracking-wider text-ink">
          Durga Assistant · {turns.length} refinement{turns.length === 1 ? "" : "s"}
        </h3>
      </div>

      {turns.length > 0 && (
        <div ref={listRef} className="mb-3 max-h-40 overflow-y-auto space-y-1.5" data-testid="assistant-history">
          {turns.map((t, i) => (
            <div key={i} className="text-xs bg-paper border border-border rounded-lg px-2.5 py-1.5 text-ink" data-testid={`assistant-turn-${i}`}>
              {t}
            </div>
          ))}
          {busy && (
            <div className="text-xs bg-marigold/10 border border-marigold/30 rounded-lg px-2.5 py-1.5 text-ink2 italic" data-testid="assistant-busy">
              Refining your site…
            </div>
          )}
        </div>
      )}

      <textarea
        value={text}
        onChange={(e) => onText(e.target.value)}
        rows={3}
        maxLength={2000}
        disabled={busy}
        placeholder="Tell Durga how to change your site."
        className="w-full bg-paper border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-marigold transition disabled:opacity-50"
        data-testid="assistant-textarea"
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter") onSend();
        }}
      />
      <button
        onClick={() => onSend()}
        disabled={busy || text.trim().length < 4}
        className="btn-primary text-xs w-full mt-2 disabled:opacity-40"
        data-testid="assistant-send"
      >
        <Sparkles className="w-3.5 h-3.5" /> {busy ? "Refining…" : "Send"} <ChevronRight className="w-3 h-3" />
      </button>

      {turns.length === 0 && !busy && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wider text-ink2 font-semibold mb-1.5">Try one</div>
          <div className="space-y-1" data-testid="assistant-samples">
            {samples.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => onText(s)}
                className="w-full text-left text-xs px-2.5 py-1.5 rounded-lg border border-border hover:border-ink hover:bg-ink hover:text-cream transition"
                data-testid={`assistant-sample-${i}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function LearningPanel({ bp, onDeepDive }) {
  const notes = bp?.learning_notes || [];
  if (notes.length === 0 && !onDeepDive) return null;
  return (
    <div className="card-soft p-4" data-testid="website-learning-panel">
      <div className="flex items-center gap-2 mb-2">
        <GraduationCap className="w-4 h-4 text-teal" />
        <h3 className="text-xs font-semibold uppercase tracking-wider text-ink">Learn this website</h3>
      </div>
      {notes.length > 0 && (
        <ul className="space-y-1.5 text-xs text-ink2 leading-relaxed mb-3">
          {notes.map((n, i) => (
            <li key={i} className="flex gap-1.5" data-testid={`website-learning-${i}`}>
              <span className="text-teal flex-shrink-0">·</span>
              <span>{n}</span>
            </li>
          ))}
        </ul>
      )}
      {onDeepDive && (
        <button
          onClick={onDeepDive}
          className="w-full text-xs px-3 py-2 rounded-lg bg-teal/10 border border-teal/30 text-teal hover:bg-teal hover:text-cream transition inline-flex items-center justify-center gap-1.5 font-semibold"
          data-testid="website-learning-deep-dive"
        >
          <GraduationCap className="w-3.5 h-3.5" /> Open full lesson
        </button>
      )}
    </div>
  );
}
